import React, { useState, useEffect } from 'react';
import api from '../api/axios'; // ajusta la ruta real según dónde esté este archivo

const COLORS = {
  bg: "#200A08",
  surface: "#341210",
  surfaceAlt: "#411713",
  orange: "#FF7A33",
  gold: "#FFC93C",
  teal: "#2EC4B6",
  ink: "#FBEDE2",
  muted: "#C9A79B",
};

const FONT_DISPLAY = '"Arial Black", "Helvetica Neue", Arial, sans-serif';
const FONT_BODY = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const FONT_MONO = 'ui-monospace, "Cascadia Code", "SFMono-Regular", Menlo, monospace';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [downloading, setDownloading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    description: '',
    technologies: '',
    demo_url: '',
    image_url: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    const handler = (e) => {
      // Ctrl + Shift + B
      if (e.ctrlKey && e.shiftKey && (e.key === 'B' || e.key === 'b')) {
        e.preventDefault();
        handleDownloadBackup();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === 'posts' ? 'posts' : 'projects';
      const res = await api.get(`/${endpoint}`);
      if (activeTab === 'posts') {
        setPosts(res.data.data || []);
      } else {
        setProjects(res.data.data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = activeTab === 'posts' ? 'posts' : 'projects';
    const method = editingItem ? 'put' : 'post';
    const url = editingItem 
      ? `/${endpoint}/${editingItem.id}`
      : `/${endpoint}`;

    try {
      let data = { ...formData };
      if (activeTab === 'projects' && formData.technologies) {
        data.technologies = formData.technologies.split(',').map(t => t.trim());
      }
      await api[method](url, data);
      setShowForm(false);
      setEditingItem(null);
      setFormData({ title: '', content: '', description: '', technologies: '', demo_url: '', image_url: '' });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este elemento?')) return;
    const endpoint = activeTab === 'posts' ? 'posts' : 'projects';
    try {
      await api.delete(`/${endpoint}/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      content: item.content || '',
      description: item.description || '',
      technologies: item.technologies ? item.technologies.join(', ') : '',
      demo_url: item.demo_url || '',
      image_url: item.image_url || ''
    });
    setShowForm(true);
  };

  const currentData = activeTab === 'posts' ? posts : projects;

  const handleDownloadBackup = async () => {
    if (downloading) return;
    const confirmar = window.confirm(
      '¿Descargar un backup completo de la base de datos?'
    );
    if (!confirmar) return;

    setDownloading(true);
    try {
      const res = await api.get('/backup', {
        responseType: 'blob', // 👈 clave para que axios no rompa el .sql
        headers: {
          'x-backup-token': process.env.REACT_APP_BACKUP_TOKEN,
        },
      });

      const blob = new Blob([res.data], { type: 'application/sql' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${new Date().toISOString().slice(0, 10)}.sql`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar backup:', error);
      alert('Error al descargar el backup');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ background: COLORS.bg, color: COLORS.ink, fontFamily: FONT_BODY }} className="min-h-screen w-full">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="relative inline-block mb-12">
          <span style={{ background: COLORS.orange, transform: "skewY(-2deg)" }} className="absolute inset-0 -z-0" />
          <h1 style={{ fontFamily: FONT_DISPLAY, color: COLORS.bg, letterSpacing: "-0.02em" }} className="relative z-10 text-4xl md:text-6xl uppercase px-3 py-1">
            Admin Panel
          </h1>
        </div>

        <div className="flex gap-4 mb-8">
          <button
            onClick={() => { setActiveTab('posts'); setShowForm(false); setEditingItem(null); }}
            style={{
              background: activeTab === 'posts' ? COLORS.orange : COLORS.surface,
              color: activeTab === 'posts' ? COLORS.bg : COLORS.ink,
              fontFamily: FONT_MONO,
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Posts
          </button>
          <button
            onClick={() => { setActiveTab('projects'); setShowForm(false); setEditingItem(null); }}
            style={{
              background: activeTab === 'projects' ? COLORS.orange : COLORS.surface,
              color: activeTab === 'projects' ? COLORS.bg : COLORS.ink,
              fontFamily: FONT_MONO,
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Proyectos
          </button>
          <button
            onClick={() => { setShowForm(!showForm); setEditingItem(null); setFormData({ title: '', content: '', description: '', technologies: '', demo_url: '', image_url: '' }); }}
            style={{
              background: COLORS.teal,
              color: COLORS.bg,
              fontFamily: FONT_MONO,
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {showForm ? 'Cancelar' : `+ Nuevo ${activeTab === 'posts' ? 'Post' : 'Proyecto'}`}
          </button>
        </div>

        {showForm && (
          <div style={{ background: COLORS.surface, borderRadius: 14, padding: '24px', marginBottom: '24px' }}>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="block text-xs uppercase mb-1">Título *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    style={{ width: '100%', padding: '10px', background: COLORS.surfaceAlt, color: COLORS.ink, border: `1px solid ${COLORS.surface}`, borderRadius: 8 }}
                    required
                  />
                </div>

                {activeTab === 'posts' && (
                  <div>
                    <label style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="block text-xs uppercase mb-1">Contenido *</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows="6"
                      style={{ width: '100%', padding: '10px', background: COLORS.surfaceAlt, color: COLORS.ink, border: `1px solid ${COLORS.surface}`, borderRadius: 8 }}
                      required
                    />
                  </div>
                )}

                {activeTab === 'projects' && (
                  <>
                    <div>
                      <label style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="block text-xs uppercase mb-1">Descripción</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows="3"
                        style={{ width: '100%', padding: '10px', background: COLORS.surfaceAlt, color: COLORS.ink, border: `1px solid ${COLORS.surface}`, borderRadius: 8 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="block text-xs uppercase mb-1">Tecnologías (separadas por coma)</label>
                      <input
                        type="text"
                        value={formData.technologies}
                        onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
                        placeholder="React, Node.js, PostgreSQL"
                        style={{ width: '100%', padding: '10px', background: COLORS.surfaceAlt, color: COLORS.ink, border: `1px solid ${COLORS.surface}`, borderRadius: 8 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="block text-xs uppercase mb-1">URL Demo</label>
                      <input
                        type="url"
                        value={formData.demo_url}
                        onChange={(e) => setFormData({ ...formData, demo_url: e.target.value })}
                        style={{ width: '100%', padding: '10px', background: COLORS.surfaceAlt, color: COLORS.ink, border: `1px solid ${COLORS.surface}`, borderRadius: 8 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="block text-xs uppercase mb-1">URL Imagen</label>
                      <input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        style={{ width: '100%', padding: '10px', background: COLORS.surfaceAlt, color: COLORS.ink, border: `1px solid ${COLORS.surface}`, borderRadius: 8 }}
                      />
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  style={{ background: COLORS.gold, color: COLORS.bg, fontFamily: FONT_DISPLAY, padding: '12px 24px', borderRadius: 8, border: 'none', cursor: 'pointer' }}
                >
                  {editingItem ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="text-center py-8">Cargando...</div>
        ) : (
          <div className="space-y-4">
            {currentData.map(item => (
              <div
                key={item.id}
                style={{ background: COLORS.surfaceAlt, borderRadius: 8, padding: '16px', border: `1px solid ${COLORS.surface}` }}
                className="flex justify-between items-center"
              >
                <div>
                  <h3 style={{ fontFamily: FONT_DISPLAY }}>{item.title}</h3>
                  <p style={{ fontFamily: FONT_MONO, color: COLORS.muted, fontSize: '12px' }}>
                    {item.created_at && new Date(item.created_at).toLocaleDateString()}
                    {activeTab === 'projects' && item.technologies && ` • ${item.technologies.join(', ')}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    style={{ background: COLORS.teal, color: COLORS.bg, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer' }}
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    style={{ background: COLORS.orange, color: COLORS.bg, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer' }}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;